import * as React from 'react'; // eslint-disable-line
import styled, { keyframes } from 'styled-components';

import { COLOR, FONT, FONT_SIZE, SPEED, THEME } from '../../packages/constants';
import {
    StyledModal,
    StyledModalTitleBar,
    StyledModalContent,
    StyledButton,
    StyledButtonGroup,
    ButtonGroupType,
} from '../modal';
import { SearchFilter } from '../search-filter/search-filter';
import ChartGroup, {
    ChartGroupWrapper,
    StyledList,
    StyledListItem,
    ChartGroupTitle,
    HorizontalRule,
    StyledGroupItem,
    ChartGroupScroller,
} from './atoms/chart-group/chart-group';
import ChartIcon, { StyledChartWrapper } from './atoms/chart-icon/chart-icon';
import ChartInfo from './atoms/chart-info/chart-info';

export interface ChartSelectorProps {
    chartData: any;
    assistAccess: boolean;
    hideFavourites?: boolean;
    hideRecent?: boolean;
    imagePath?: string;
    existingChart?: string;
    handleCancelButton?: (e: any) => void;
    handlePrimaryButton?: (e: any) => void;
    themeProvider?: {};
    hasModernStyles?: boolean;
}

interface StyledContentProps {
    isChartSelected: boolean;
    imagePath?: string;
}

const DefaultGroups = {
    NewChart: 'create-a-new-chart',
    NewTextPanel: 'create-a-new-text-panel',
    Recent: 'recent',
    Favourites: 'favourites',
    All: 'all',
};

const exampleAnimation = keyframes`
    from {
        width: 0;
    }
    
    to {
        width: 240px;
    }
`;

const StyledChartGroup = styled(ChartGroup)``;

const StyledChartInfo = styled(ChartInfo)<StyledContentProps>`
    overflow: hidden;
    width: ${(props: StyledContentProps) => (props.isChartSelected ? '240px' : '0')};
    animation: ${exampleAnimation} ${SPEED.TRANSITION} ease-in 1 forwards;
`;

const StyledContent = styled.div<StyledContentProps>`
    background-color: ${COLOR.PRIMARY.WHITE};
    height: 100%;
    transition: all ${SPEED.TRANSITION} ease-in;
    width: ${(props: StyledContentProps) => (props.isChartSelected ? '420px' : '660px')};
`;

const StyledChartSelector = styled.div<ChartSelectorProps>`
    display: flex;
    height: 100%;
    width: 900px;

    ${StyledChartGroup} {
        max-width: 240px;
        width: 240px;
        min-width: 240px;
    }
`;

const StyledFilterWrapper = styled.div`
    background-color: ${COLOR.PRIMARY.WHITE};
    padding: 22px;
`;

const StyledMessage = styled.p`
    font-family: ${FONT.OPEN_SANS};
    font-size: ${FONT_SIZE.MEDIUM};
    text-align: center;
    color: ${COLOR.MAIN.FORM_FIELD_DARK};
    padding-top: 18vh;
    width: 100%;
`;

export const ChartSelector = React.forwardRef<HTMLDivElement, ChartSelectorProps>(
    (
        {
            chartData,
            hideFavourites,
            hideRecent,
            assistAccess,
            imagePath,
            existingChart,
            handleCancelButton,
            handlePrimaryButton,
            hasModernStyles,
            ...props
        },
        ref,
    ) => {
        const [groupItemChecked, setGroupItemChecked] = React.useState(DefaultGroups.All);
        const [filterValue, setFilterValue] = React.useState('');
        const [chartList, setChartList] = React.useState([]);
        const [ignoreScrollIntoView, setIgnoreScrollIntoView] = React.useState(false);
        // tslint:disable-next-line:no-null-keyword
        const filterInputRef = React.useRef<HTMLInputElement>(null);
        // tslint:disable-next-line:no-null-keyword
        const chartWrapperRef = React.useRef<HTMLDivElement>(null);

        // create an array of all the charts
        const chartsArray: any = [];

        // if the user is changing a chart (chart is already selected), set as temp variable
        let temporaryChartSelected: any = {};

        // loop through all the charts and unselect each
        const unselectAllCharts = () => {
            Object.keys(chartData).map((groupTitle) =>
                Object.keys(chartData[groupTitle].groups).map((groupItem) =>
                    Object.keys(chartData[groupTitle].groups[groupItem].charts).map((chartItem) => {
                        const chart = chartData[groupTitle].groups[groupItem].charts[chartItem];
                        return (chart.selected = false);
                    }),
                ),
            );
        };

        // loop through all of the charts and build chartsArray
        const renderCharts = (groupItemSelected: any) => {
            if (groupItemSelected === DefaultGroups.All) {
                Object.keys(chartData).map((groupTitle) =>
                    Object.keys(chartData[groupTitle].groups).map((groupItem) =>
                        Object.keys(chartData[groupTitle].groups[groupItem].charts).map((chartItem) => {
                            const chart = chartData[groupTitle].groups[groupItem].charts[chartItem];
                            // if a chart is selected, add it to the temp variable
                            if (chart.selected) {
                                temporaryChartSelected = chart;
                            }
                            return chartsArray.push(chart);
                        }),
                    ),
                );
                // TODO: add favourites & recent
                // } else if (groupItemSelected === DefaultGroups.Favourites) {
                //     console.log('handle favourites'); // de-scoped for the time being
                // } else if (groupItemSelected === DefaultGroups.Recent) {
                //     console.log('handle recent'); // de-scoped for the time being
            } else if (groupItemSelected === DefaultGroups.NewChart) {
                chartsArray.push(
                    {
                        title: 'Custom Pie Chart',
                        description:
                            "If you can't find the pie chart you're looking for, you can easily build your own.",
                        reportCodes: [],
                        isFavourite: false,
                        iconName: 'custom-pie',
                        id: 'CustomPie',
                    },
                    {
                        title: 'Custom Chart',
                        description: "If you can't find the chart you're looking for, you can easily build your own.",
                        reportCodes: [],
                        isFavourite: false,
                        iconName: 'custom-line',
                        id: 'Custom',
                    },
                );
            } else if (groupItemSelected === DefaultGroups.NewTextPanel) {
                chartsArray.push({
                    title: 'Text Panel',
                    description: 'Add text to your page, customise colours, and include auto-text or custom tags.',
                    reportCodes: [],
                    isFavourite: false,
                    iconName: 'text-panel',
                    id: 'TextPanel',
                    type: 'TEXT',
                });
            }
        };
        renderCharts(groupItemChecked);

        // set the initial states for selected chart & isChartSelected, if one is selected
        const [selectedChart, setSelectedChart] = React.useState(temporaryChartSelected);
        const [isChartSelected, setIsChartSelected] = React.useState(
            Object.prototype.hasOwnProperty.call(temporaryChartSelected, 'title'),
        );

        // handle click when user clicks on chart icon
        const handleChartIconChange = (chart: any) => {
            unselectAllCharts();
            chart.selected = true;
            setSelectedChart(chart);
            setIsChartSelected(true);
            setIgnoreScrollIntoView(true);
        };

        // handle click when user clicks on left column group name
        const handleGroupItemClick = (event: any, charts?: any) => {
            setIgnoreScrollIntoView(true);
            setGroupItemChecked(event.target.value);
            if (event.target.value !== DefaultGroups.All) {
                setFilterValue('');
            }
            setChartList(charts);
            // @ts-ignore
            chartWrapperRef.current.scrollTop = 0;
        };

        // handle change when user starts typing in the search-filter
        const handleFilterChange = (event: any) => {
            setFilterValue(event.target.value.toLowerCase());
            setGroupItemChecked(DefaultGroups.All);
        };

        // handle click when user clicks the clear search-filter button
        const handleFilterClearButton = () => {
            setFilterValue('');
            // @ts-ignore
            filterInputRef.current.focus();
        };

        // searchable charts that contains the search string in the title
        const filteredData = chartsArray.filter((item: any) => {
            const isSearching = filterValue.trim().length > 0;
            const isSearchable = !(item.hideInSearch && isSearching);
            const isSearchStringInTitle = item.title.toLowerCase().includes(filterValue);
            return Object.keys(item).some(() => isSearchable && isSearchStringInTitle);
        });

        // scroll to selected chart
        React.useEffect(() => {
            const selectedChart = document.querySelector(`input[value="${temporaryChartSelected.id}"]`);
            // only scroll the view if there is a preselected chart, dont scroll if user clicks a chart
            if (selectedChart && !ignoreScrollIntoView) {
                selectedChart.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            }
        });

        return (
            <>
                <StyledModal>
                    <StyledModalTitleBar>{existingChart ? 'Change Chart' : 'Add Chart'}</StyledModalTitleBar>
                    <StyledModalContent>
                        <StyledChartSelector
                            imagePath={imagePath}
                            assistAccess={assistAccess}
                            chartData={chartData}
                            hideFavourites={hideFavourites}
                            hideRecent={hideRecent}
                            ref={ref}
                            {...props}
                        >
                            <ChartGroupWrapper {...props}>
                                <StyledList>
                                    <StyledGroupItem
                                        onChange={(e) => handleGroupItemClick(e)}
                                        radioValue={DefaultGroups.NewChart}
                                        checked={groupItemChecked === DefaultGroups.NewChart}
                                        assistAccess={assistAccess}
                                        dataTestId={'chart-group-name__create-a-new-chart'}
                                    >
                                        Create a new chart
                                    </StyledGroupItem>
                                    <StyledGroupItem
                                        onChange={(e) => handleGroupItemClick(e)}
                                        radioValue={DefaultGroups.NewTextPanel}
                                        checked={groupItemChecked === DefaultGroups.NewTextPanel}
                                        assistAccess={assistAccess}
                                        dataTestId={'chart-group-name__create-a-new-text-panel'}
                                    >
                                        Create a text panel
                                    </StyledGroupItem>
                                    {!hideRecent && (
                                        <StyledGroupItem
                                            onChange={(e) => handleGroupItemClick(e)}
                                            radioValue={DefaultGroups.Recent}
                                            checked={groupItemChecked === DefaultGroups.Recent}
                                            assistAccess={assistAccess}
                                            dataTestId={'chart-group-name__recent'}
                                        >
                                            Recent
                                        </StyledGroupItem>
                                    )}
                                    {!hideFavourites && (
                                        <StyledGroupItem
                                            onChange={(e) => handleGroupItemClick(e)}
                                            radioValue={DefaultGroups.Favourites}
                                            checked={groupItemChecked === DefaultGroups.Favourites}
                                            assistAccess={assistAccess}
                                            dataTestId={'chart-group-name__favourites'}
                                        >
                                            Favourites
                                        </StyledGroupItem>
                                    )}
                                </StyledList>

                                <HorizontalRule />

                                <ChartGroupScroller>
                                    <StyledList>
                                        <StyledGroupItem
                                            onChange={(e) => handleGroupItemClick(e)}
                                            radioValue={DefaultGroups.All}
                                            checked={groupItemChecked === DefaultGroups.All}
                                            assistAccess={assistAccess}
                                            dataTestId={'chart-group-name__all'}
                                        >
                                            All charts...
                                        </StyledGroupItem>
                                        {chartData &&
                                            Object.keys(chartData).map((groupTitle: any) => {
                                                return (
                                                    <StyledListItem key={`group-title-${groupTitle}`}>
                                                        {
                                                            <ChartGroupTitle>
                                                                {chartData[groupTitle].title}
                                                            </ChartGroupTitle>
                                                        }
                                                        <StyledList>
                                                            {Object.keys(chartData[groupTitle].groups).map(
                                                                (groupItem) => {
                                                                    const group =
                                                                        chartData[groupTitle].groups[groupItem];
                                                                    return (
                                                                        <StyledGroupItem
                                                                            onChange={(e) =>
                                                                                handleGroupItemClick(e, group.charts)
                                                                            }
                                                                            radioValue={group.id}
                                                                            key={`group-item-${groupItem}`}
                                                                            checked={groupItemChecked === group.id}
                                                                            assistAccess={assistAccess}
                                                                            dataTestId={`chart-group-name__${group.id}`}
                                                                        >
                                                                            {group.title}
                                                                        </StyledGroupItem>
                                                                    );
                                                                },
                                                            )}
                                                        </StyledList>
                                                    </StyledListItem>
                                                );
                                            })}
                                    </StyledList>
                                </ChartGroupScroller>
                            </ChartGroupWrapper>
                            <StyledContent isChartSelected={isChartSelected}>
                                <StyledFilterWrapper>
                                    <SearchFilter
                                        placeholder={'Filter by chart'}
                                        assistAccess={assistAccess}
                                        handleChange={handleFilterChange}
                                        handleClearButton={handleFilterClearButton}
                                        searchTerm={filterValue}
                                        ref={filterInputRef}
                                    />
                                </StyledFilterWrapper>
                                <StyledChartWrapper ref={chartWrapperRef}>
                                    {Object.values(DefaultGroups).includes(groupItemChecked) ? (
                                        filteredData.length >= 1 ? (
                                            filteredData.map((chart: any) => {
                                                return (
                                                    <ChartIcon
                                                        imagePath={imagePath}
                                                        iconName={chart.iconName}
                                                        iconLabel={chart.title}
                                                        id={chart.id}
                                                        onChange={() => handleChartIconChange(chart)}
                                                        key={chart.id}
                                                        assistAccess={assistAccess}
                                                        isSelected={chart.selected}
                                                        dataTestId={`chart-icon__${chart.id}`}
                                                        hasModernStyles={hasModernStyles}
                                                    />
                                                );
                                            })
                                        ) : (
                                            <StyledMessage>
                                                {groupItemChecked === DefaultGroups.Recent
                                                    ? 'No charts have been used recently'
                                                    : groupItemChecked === DefaultGroups.Favourites
                                                    ? 'No favourites have been added'
                                                    : `No charts found matching "${filterValue}"`}
                                            </StyledMessage>
                                        )
                                    ) : (
                                        chartList.map((chart: any) => {
                                            return (
                                                <ChartIcon
                                                    imagePath={imagePath}
                                                    iconName={chart.iconName}
                                                    iconLabel={chart.title}
                                                    id={chart.id}
                                                    onChange={() => handleChartIconChange(chart)}
                                                    key={chart.id}
                                                    assistAccess={assistAccess}
                                                    isSelected={chart.selected}
                                                    dataTestId={`chart-icon__${chart.id}`}
                                                    hasModernStyles={hasModernStyles}
                                                />
                                            );
                                        })
                                    )}
                                </StyledChartWrapper>
                            </StyledContent>

                            {isChartSelected && (
                                <StyledChartInfo
                                    isChartSelected={isChartSelected}
                                    imagePath={imagePath}
                                    iconName={selectedChart.iconName}
                                    chartTitle={selectedChart.title}
                                    assistAccess={assistAccess}
                                    description={selectedChart.description}
                                    reportCodes={selectedChart.reportCodes}
                                    hideFavourites={hideFavourites}
                                />
                            )}
                        </StyledChartSelector>
                    </StyledModalContent>
                    <StyledButtonGroup buttonGroupType={ButtonGroupType.Right}>
                        <StyledButton
                            dataId={'chart-selector__button--add'}
                            onClick={handlePrimaryButton}
                            disabled={!isChartSelected || existingChart === selectedChart.id}
                            value={JSON.stringify(selectedChart)}
                        >
                            {existingChart ? 'Select' : 'Add'}
                        </StyledButton>
                        <StyledButton
                            theme={THEME.GENERIC}
                            dataId={'chart-selector__button--cancel'}
                            onClick={handleCancelButton}
                        >
                            Cancel
                        </StyledButton>
                    </StyledButtonGroup>
                </StyledModal>
            </>
        );
    },
);
ChartSelector.displayName = 'ChartSelector';
